import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Wrench,
  RefreshCw,
  Fan,
  Wind,
  Thermometer,
  Droplets,
  ArrowRight,
} from "lucide-react";
import { useSeo } from "@/lib/useSeo";
import PageHero from "@/components/site/PageHero";
import FinalCTA from "@/components/site/FinalCTA";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/dd575c7557e7d72528f8d94bb53752a0ac5d546bb142e6d9098a6c4d0ab73cf8.png";
const COND_IMG =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/71d54c90826a94d279928daf40ede643ab55beaac8e7b5b511f1b72407351e92.png";
const FILTER_IMG =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/6c0f367cca2954e37cc43df12dabc27ab5ae3d2225313ab53fc49411ef2a492c.png";
const VENT_IMG =
  "https://static.prod-images.emergentagent.com/jobs/51ddf5f6-e80c-4259-9ef6-c5393e7b5903/images/27eabd4237fe10deaf59000323d381461289c590eada597fe702c2fbe8b63aca.png";

const MAIN_SERVICES = [
  {
    slug: "ac-repair",
    to: "/services/ac-repair",
    icon: Wrench,
    title: "AC Repair",
    desc: "AC not cooling, leaking water, short cycling, or making strange sounds — start a clear service request with everything the contractor needs.",
    img: COND_IMG,
    alt: "Closeup of an outdoor AC condenser unit beside a Southwest Florida home.",
  },
  {
    slug: "maintenance",
    to: "/services/maintenance",
    icon: RefreshCw,
    title: "Maintenance & Tune-Ups",
    desc: "Pre-summer tune-ups, drain line flushes, coil cleaning, and a maintenance plan concept built for Florida humidity.",
    img: FILTER_IMG,
    alt: "Replacing a clean HVAC air filter — part of seasonal maintenance.",
  },
  {
    slug: "replacement",
    to: "/services/replacement",
    icon: Fan,
    title: "System Replacement",
    desc: "When repair stops adding up — right-sized swap-outs, efficiency basics, and a quote-ready intake that respects your time.",
    img: HERO_IMG,
    alt: "Southwest Florida home at golden hour showing an HVAC system in residential context.",
  },
  {
    slug: "indoor-air-quality",
    to: "/services/indoor-air-quality",
    icon: Wind,
    title: "Indoor Air Quality",
    desc: "Dehumidification, filtration, UV options, and ventilation for coastal homes that fight humidity year round.",
    img: VENT_IMG,
    alt: "Ceiling vent with subtle airflow lines representing indoor air quality.",
  },
];

const OTHER_TOPICS = [
  {
    icon: Thermometer,
    title: "Thermostat Issues",
    desc: "Wiring, schedule problems, and getting smart thermostats actually working.",
    to: "/services/ac-repair",
  },
  {
    icon: Droplets,
    title: "Weak Airflow, Drain Line & Leaks",
    desc: "Backups, dripping units, and rooms that never quite cool down.",
    to: "/services/ac-repair",
  },
];

export default function Services() {
  useSeo({
    title:
      "HVAC services for Southwest Florida homes | Sun Chaser Cooling — PrismForge Digital Demo",
    description:
      "Demo overview of HVAC services for Cape Coral, Fort Myers, Naples and the rest of Southwest Florida — AC repair, maintenance, replacement, and indoor air quality.",
  });

  return (
    <div data-testid="services-page">
      <PageHero
        eyebrow="Services"
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Services" }]}
        title="HVAC services for"
        highlight="Southwest Florida homes."
        subtitle="Repair, maintenance, replacement, and indoor air quality — four core services, each with a smarter intake flow so the contractor walks in already knowing what to bring."
        primaryCta={{ label: "Request HVAC Service", to: "/request-service" }}
        secondaryCta={{
          label: "Schedule Maintenance",
          to: "/request-service?intent=maintenance",
        }}
        variant="light"
        showDemoNotice
        testId="services-page-hero"
      />

      <section
        data-testid="services-grid-section"
        aria-labelledby="services-grid-heading"
        className="relative bg-white py-20 sm:py-24"
      >
        <div className="container-page">
          <div className="max-w-2xl">
            <span className="pill">
              <span className="h-1.5 w-1.5 rounded-full bg-aqua" />
              Core services
            </span>
            <h2
              id="services-grid-heading"
              className="mt-4 font-display text-3xl font-bold leading-tight tracking-tight text-navy sm:text-4xl"
            >
              Four featured services.
            </h2>
            <p className="mt-4 text-base text-navy/70 sm:text-lg">
              Each one has a dedicated detail page and a tailored service-request
              flow.
            </p>
          </div>

          <motion.ul
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
            variants={{
              hidden: {},
              show: { transition: { staggerChildren: 0.08 } },
            }}
            className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-2"
          >
            {MAIN_SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <motion.li
                  key={s.slug}
                  variants={{
                    hidden: { opacity: 0, y: 14 },
                    show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
                  }}
                >
                  <Link
                    to={s.to}
                    data-testid={`services-overview-card-${s.slug}`}
                    className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-mist-line bg-white shadow-soft transition-all hover:-translate-y-1 hover:border-aqua/45 hover:shadow-cyanGlow"
                  >
                    <div className="relative aspect-[16/9] overflow-hidden">
                      <img
                        src={s.img}
                        alt={s.alt}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-navy/55 via-navy/0 to-transparent" aria-hidden="true" />
                      <span className="absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-navy/70 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
                        <Icon className="h-3 w-3 text-aqua" />
                        {s.title}
                      </span>
                    </div>
                    <div className="flex flex-1 flex-col p-6 sm:p-7">
                      <h3 className="font-display text-2xl font-bold leading-tight tracking-tight text-navy">
                        {s.title}
                      </h3>
                      <p className="mt-2 text-[15px] leading-relaxed text-navy/70">
                        {s.desc}
                      </p>
                      <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-aqua-deep transition-transform group-hover:translate-x-0.5">
                        Read more about {s.title.toLowerCase()}
                        <ArrowRight className="h-4 w-4" />
                      </span>
                    </div>
                  </Link>
                </motion.li>
              );
            })}
          </motion.ul>

          {/* Other topics */}
          <div className="mt-16">
            <h3 className="font-display text-xl font-bold tracking-tight text-navy">
              Other common issues we cover
            </h3>
            <p className="mt-1 text-sm text-navy/60">
              These topics live inside the AC Repair flow for now.
            </p>
            <ul className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {OTHER_TOPICS.map((t) => {
                const Icon = t.icon;
                const slug = t.title
                  .toLowerCase()
                  .replace(/&/g, "and")
                  .replace(/[^a-z0-9]+/g, "-")
                  .replace(/(^-|-$)/g, "");
                return (
                  <li key={t.title}>
                    <Link
                      to={t.to}
                      data-testid={`services-overview-topic-${slug}`}
                      className="group flex items-center gap-4 rounded-2xl border border-mist-line bg-white p-5 shadow-soft transition-all hover:-translate-y-0.5 hover:border-aqua/40"
                    >
                      <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-navy text-aqua">
                        <Icon className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="font-display text-base font-bold text-navy">
                          {t.title}
                        </p>
                        <p className="mt-0.5 text-sm text-navy/65">{t.desc}</p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-aqua-deep transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      <FinalCTA
        title="Not sure which service you need?"
        subtitle="Start a service request — describe what's happening and we'll route it to the right path."
      />
    </div>
  );
}
